import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { fetchMissions, updateMission, getMissionByGuid } from '../services/missionService';
import { Link } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import CONFIG from '../config/config';
import StartIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import UnlockIcon from '@mui/icons-material/LockOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import PropellerIcon from '@mui/icons-material/Autorenew';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import AddIcon from '@mui/icons-material/Add';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RemoveIcon from '@mui/icons-material/Remove';
import { v4 as uuidv4 } from 'uuid';
import { getSubscriptionByGuid } from '../services/subscriptionService';

import {
  FlightTakeoff as MissionIcon,
  List as ListIcon,
  Map as MapIcon,
  LocationOn as MapPinIcon,
} from '@mui/icons-material';

import {
  Menu,
  MenuItem,
  ListItemIcon,
  Button,
  TextField,
  Select,
  MenuItem as DropdownMenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Checkbox,
  LinearProgress,
  Divider,
  Tooltip,
} from '@mui/material';

const getDateTime = (dateString) => {
  if (!dateString) return ['-'];
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return ['Invalid Date'];

  const datePart = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const timePart = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    //timeZoneName: 'short',
  });

  return [datePart, timePart];
};

const getMissionLocation = (mission) => mission.jsonPlaceData?.preferredAddress || 'N/A';

const Missions = () => {
  let primaryColor = '#2c3e50';
  const { selectedOrg, updateSelectedOrg } = useOrganization();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('');
  const [filteredMissions, setFilteredMissions] = useState([]);
  const [selectedMissions, setSelectedMissions] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();
  const [isDeterminingLocation, setIsDeterminingLocation] = useState(false);
  const [missionsTrigger, setMissionsTrigger] = useState(0);

  const groupedMissions = filteredMissions.reduce((acc, mission) => {
    const group = mission.groupGuid || 'Ungrouped';
    if (!acc[group]) acc[group] = [];
    acc[group].push(mission);
    return acc;
  }, {});

  const sortedGroupedMissions = Object.entries(groupedMissions).sort(([groupA], [groupB]) => {
    if (groupA === 'Ungrouped') return 1; // Push 'Ungrouped' to the end
    if (groupB === 'Ungrouped') return -1; // Keep groups at the top
    return groupA.localeCompare(groupB); // Optional: Sort groups alphabetically
  });

  const groupColors = [
    '#3f51b5', // Blue
    '#ff9800', // Orange
    '#4caf50', // Green
    '#f44336', // Red
    '#9c27b0', // Purple
    '#00bcd4', // Cyan
  ];

  const groupColorMap = new Map();

  const getColorForGroup = (groupGuid) => {
    if (!groupGuid || groupGuid === 'Ungrouped') return 'transparent';

    // If the group already has a color assigned, return it
    if (groupColorMap.has(groupGuid)) {
      return groupColorMap.get(groupGuid);
    }

    // Assign the next available color from the circular queue
    const color = groupColors[groupColorMap.size % groupColors.length];
    groupColorMap.set(groupGuid, color);

    return color;
  };

  useEffect(() => {
    //console.log('Is determining location:', isDeterminingLocation);
  }, [isDeterminingLocation]);

  useEffect(() => {
    const getMissions = async () => {
      if (!selectedOrg) {
        setError('No organization selected.');
        setLoading(false);
        return;
      }

      // Clean up existing markers before fetching new missions
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      try {
        const data = await fetchMissions(selectedOrg);
        const filteredData = data.filter(mission => !mission.deletedAt); // Remove deleted missions
        const sortedMissions = filteredData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setMissions(sortedMissions || []);
        setFilteredMissions(sortedMissions);
        setError(null);
      } catch (err) {
        setError('Failed to fetch missions');
      } finally {
        setLoading(false);
      }
    };

    getMissions();

    return () => {
      // Clean up markers on selectedOrg change
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
    };
  }, [selectedOrg, missionsTrigger]);

  useEffect(() => {
    if (view === 'map' && mapContainerRef.current && !mapRef.current) {
      console.log('Initializing map...');

      // Retrieve saved coordinates and zoom from sessionStorage
      const savedLat = sessionStorage.getItem('lastLat');
      const savedLon = sessionStorage.getItem('lastLon');
      const savedZoom = sessionStorage.getItem('lastZoom');

      // Use saved coordinates and zoom or fallback to defaults
      const fallbackCenter = savedLat && savedLon
        ? [parseFloat(savedLon), parseFloat(savedLat)]
        : [-98.5795, 39.8283]; // Default to US center
      const fallbackZoom = savedZoom ? parseFloat(savedZoom) : 10; // Default zoom level

      // Initialize the map instance
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: fallbackCenter,
        zoom: fallbackZoom,
        fadeDuration: 0, // Disable fading for faster appearance
        accessToken: CONFIG.MAPBOX.PUBLIC_TOKEN,
      });

      setIsDeterminingLocation(true); // Start showing the progress indicator

      if (!savedLat || !savedLon) {
        // Center map on user location if available
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;

            // Use the current zoom level if available
            const currentZoom = getZoom();

            mapRef.current.jumpTo({
              center: [longitude, latitude],
              zoom: currentZoom, // Preserve the current zoom level
              duration: 2000, // Optional: Adjust duration for smoother animation
              easing: (t) => t * (2 - t), // Optional: Add easing for smoothness
            });

            // Save the user's location to sessionStorage
            sessionStorage.setItem('lastLat', latitude.toString());
            sessionStorage.setItem('lastLon', longitude.toString());
            sessionStorage.setItem('lastZoom', currentZoom.toString()); // Save the current zoom level

            setIsDeterminingLocation(false);
          },
          (error) => {
            console.error('Error fetching location:', error);
            alert('Unable to fetch your location. Please enable location services.');
            setIsDeterminingLocation(false);
          }
        );
      } else {
        // Stop the location indicator if a saved location is used
        setIsDeterminingLocation(false);
      }

      // Add navigation controls
      const navControl = new mapboxgl.NavigationControl();
      mapRef.current.addControl(navControl, 'top-right');

      // Save the map's center and zoom on move
      mapRef.current.on('moveend', () => {
        const { lat, lng } = mapRef.current.getCenter();
        const zoom = getZoom();
        sessionStorage.setItem('lastLat', lat.toString());
        sessionStorage.setItem('lastLon', lng.toString());
        sessionStorage.setItem('lastZoom', zoom.toString());
      });

      // Cleanup function
      return () => {
        if (mapRef.current) {
          console.log('Cleaning up map...');
          mapRef.current.remove();
          mapRef.current = null;
        }

        markersRef.current.forEach((marker) => marker.remove());
        markersRef.current = [];
      };
    }
  }, [view]);

  const handleCenterOnLocation = () => {
    if (!mapRef.current) {
      console.warn('Map instance is not initialized.');
      return;
    }

    setIsDeterminingLocation(true); // Start showing the progress indicator

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        // Safely get the current zoom level or default to 10
        const currentZoom = getZoom();

        mapRef.current.jumpTo({
          center: [longitude, latitude],
          zoom: currentZoom, // Use current or saved zoom level
          duration: 2000, // Smooth transition
          easing: (t) => t * (2 - t), // Optional: Add easing for smoothness
        });

        // Save the updated location and zoom to sessionStorage
        sessionStorage.setItem('lastLat', latitude.toString());
        sessionStorage.setItem('lastLon', longitude.toString());
        sessionStorage.setItem('lastZoom', currentZoom.toString());

        setIsDeterminingLocation(false);
      },
      (error) => {
        console.error('Error fetching location:', error);
        alert('Unable to fetch your location. Please enable location services.');
        setIsDeterminingLocation(false);
      }
    );
  };

  // Function to safely retrieve the current zoom level
  const getZoom = () => {
    if (!mapRef.current) {
      console.warn('Map instance is not initialized.');
      return parseFloat(sessionStorage.getItem('lastZoom')) || 10; // Default zoom level
    }
    return mapRef.current.getZoom();
  };

  const handleViewDetails = (missionGuid) => {
    navigate(`/mission/${missionGuid}`);
  };

  useEffect(() => {
    if (!mapRef.current || view !== 'map') return;

    // Clean up existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Add new markers
    filteredMissions.forEach((mission) => {
      if (mission.lat && mission.lon) {
        const popupContent = document.createElement('div');
        popupContent.style.fontSize = '14px';
        popupContent.style.color = primaryColor;
        popupContent.style.padding = '10px'; // Add padding for a better layout
        popupContent.style.borderRadius = '8px'; // Round the corners
        popupContent.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)'; // Add a subtle shadow
        popupContent.style.backgroundColor = '#ffffff'; // White background
        popupContent.style.textAlign = 'left'; // Left-align text

        const missionTitle = document.createElement('strong');
        missionTitle.textContent = `Mission: ${getPlainMissionName(mission)}`;
        missionTitle.style.display = 'block'; // Ensure it occupies its own line
        missionTitle.style.marginBottom = '8px'; // Add spacing below the title
        missionTitle.style.color = '#007bff'; // Highlight the title
        missionTitle.style.cursor = 'pointer';
        missionTitle.addEventListener('click', () => handleViewDetails(mission.missionGuid));
        popupContent.appendChild(missionTitle);

        const location = document.createElement('p');
        location.textContent = `Location: ${getMissionLocation(mission)}`;
        location.style.margin = '0'; // Remove default margin
        location.style.color = '#6c757d'; // Subdued text color for secondary info
        popupContent.appendChild(location);

        const status = document.createElement('p');
        status.textContent = `Status: ${getStatus(mission)}`;
        status.style.margin = '0'; // Remove default margin
        status.style.marginTop = '4px'; // Add a little spacing above
        status.style.color = '#6c757d'; // Subdued text color for secondary info
        popupContent.appendChild(status);

        const viewDetailsButton = document.createElement('button');
        viewDetailsButton.textContent = 'View Details';
        viewDetailsButton.style.display = 'inline-block';
        viewDetailsButton.style.marginTop = '10px'; // Add spacing above the button
        viewDetailsButton.style.padding = '6px 12px';
        viewDetailsButton.style.backgroundColor = '#007bff';
        viewDetailsButton.style.color = '#ffffff';
        viewDetailsButton.style.border = 'none';
        viewDetailsButton.style.borderRadius = '4px';
        viewDetailsButton.style.cursor = 'pointer';
        viewDetailsButton.style.fontSize = '12px';
        viewDetailsButton.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
        viewDetailsButton.addEventListener('click', () => handleViewDetails(mission.missionGuid));
        popupContent.appendChild(viewDetailsButton);

        const popup = new mapboxgl.Popup({ offset: 25 }).setDOMContent(popupContent);
        const marker = new mapboxgl.Marker({ color: primaryColor })
          .setLngLat([mission.lon, mission.lat])
          .setPopup(popup) // Attach the popup to the marker
          .addTo(mapRef.current);
        markersRef.current.push(marker);
      }
    });

    // Cleanup function
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
    };
  }, [filteredMissions, view]);

  useEffect(() => {
    const filtered = missions.filter((mission) => {
      const missionName =
        mission.missionName || mission.jsonPlaceData?.preferredAddress || '';
      const matchesSearch = missionName
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const status = getStatus(mission);
      const matchesFilter = !filter || status.toLowerCase() === filter.toLowerCase();
      return matchesSearch && matchesFilter;
    });

    setFilteredMissions(filtered);
  }, [missions, searchTerm, filter]);

  const renderIconsForState = (mission) => {
    const iconMapping = {
      'In Progress': {
        icon: (
          <PropellerIcon
            sx={{
              fontSize: '20px',
              animation: mission.startedAt && !mission.completedAt ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        ),
        isActive: mission.startedAt && !mission.completedAt,
      },
      'Completed': {
        icon: (
          <Tooltip title="Mission completed" arrow>
            <FlightLandIcon
              sx={{ fontSize: '20px' }}
            />
          </Tooltip>
        ),
        isActive: !!mission.completedAt,
      },
      'Locked': {
        icon: (
          <Tooltip title="Mission is locked" arrow>
            <LockIcon
              sx={{ fontSize: '20px' }}
            />
          </Tooltip>
        ),
        isActive: !!mission.isLocked,
      },
      'Deleted': {
        icon: (
          <Tooltip title="Mission has been deleted" arrow>
            <DeleteIcon
              sx={{ fontSize: '20px' }}
            />
          </Tooltip>
        ),
        isActive: !!mission.deletedAt,
      },
      /*
      'Verified': {
        icon: (
          <Tooltip title="Mission verified" arrow>
            <VerifiedIcon
              sx={{ fontSize: '20px' }}
            />
          </Tooltip>
        ),

        isActive: !!mission.completedAt,
      },
      */
      'Unassigned': {
        icon: (
          <Tooltip title="This mission is not assigned to an organization." arrow>
            <WarningIcon
              sx={{ fontSize: '20px', color: 'red' }}
            />
          </Tooltip>
        ),
        isActive: !mission.subscriptionGuid,
      },
    };

    const orderedStates = ['Unassigned', 'In Progress', 'Completed', 'Locked', 'Deleted', 'Verified'];

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {orderedStates.map((state) => {
          const entry = iconMapping[state];

          // Ensure valid `icon` and `isActive` before rendering
          if (!entry || !entry.icon) {
            console.warn(`Missing or invalid icon for state: ${state}`);
            return null;
          }

          const { icon, isActive } = entry;

          return isActive
            ? React.cloneElement(icon, {
              key: state,
              sx: {
                ...icon.props.sx,
                color: isActive ? icon.props.sx?.color || primaryColor : '#dcdcdc', // Active or gray color
              },
            })
            : null;
        })}
      </Box>
    );
  };

  const getMissionName = (mission) => {
    return (
      <Link
        to={`/mission/${mission.missionGuid}`}
        style={{
          color: '#007bff',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        {mission.missionName || mission.jsonPlaceData?.preferredAddress || 'N/A'}
      </Link>
    );
  };

  const getPlainMissionName = (mission) => {
    return mission.missionName || mission.jsonPlaceData?.preferredAddress || 'N/A';
  };

  const getStatus = (mission) => {
    if (!mission.startedAt) return 'Not Started';
    if (mission.startedAt && !mission.completedAt) return 'In Progress';
    return 'Completed';
  };

  const handleSelectMission = (missionGuid) => {
    setSelectedMissions((prevSelected) =>
      prevSelected.includes(missionGuid)
        ? prevSelected.filter((id) => id !== missionGuid)
        : [...prevSelected, missionGuid]
    );
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedMissions([]);
      setAllSelected(false);
    } else {
      setSelectedMissions(filteredMissions.map((mission) => mission.missionGuid));
      setAllSelected(true);
    }
  };

  const handleActionsMenuOpen = (event) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleAction = async (action) => {
    //console.log(`Performing action: ${action} on selected missions`, selectedMissions);

    const timestamp = new Date().toISOString();
    let hasLockedMissions = false; // Track if any mission is locked
    let hasDeletedMissions = false; // Track if any mission is deleted

    try {
      // Loop through selected missions to update each one based on the action
      for (const missionGuid of selectedMissions) {
        const mission = missions.find((m) => m.missionGuid === missionGuid);

        if (!mission) {
          console.error(`Mission with missionGuid ${missionGuid} not found.`);
          continue;
        }

        let updateData = {
          version: mission.version || 1,
          modifiedFields: {},
        };

        switch (action.toLowerCase()) {
          case 'delete':
            if (mission.isLocked) {
              hasLockedMissions = true; // Flag if a locked mission is encountered
              continue; // Skip the delete action for this mission
            }
            hasDeletedMissions = true;
            updateData.modifiedFields.deletedAt = { value: timestamp, timestamp };
            break;

          case 'start':
            updateData.modifiedFields.startedAt = { value: timestamp, timestamp };
            break;

          case 'stop':
            updateData.modifiedFields.completedAt = { value: timestamp, timestamp };
            break;

          case 'group':
            const groupGuid = uuidv4();
            updateData.modifiedFields.groupGuid = { value: groupGuid, timestamp };
            break;

          case 'ungroup':
            updateData.modifiedFields.groupGuid = { value: null, timestamp };
            break;

          case 'lock': {
            const newLockState = !mission.isLocked;
            updateData.modifiedFields.isLocked = { value: newLockState, timestamp };
            break;
          }

          default:
            console.warn(`Action '${action}' is not recognized.`);
            continue;
        }

        // Update the mission on the server
        await updateMission(missionGuid, updateData);

        // Fetch the updated mission
        const updatedMission = await getMissionByGuid(missionGuid);

        // Update the state with the refreshed mission
        setMissions((prevMissions) =>
          prevMissions.map((m) =>
            m.missionGuid === updatedMission.missionGuid ? updatedMission : m
          )
        );
      }

      // Notify the user if delete action was skipped for locked missions
      if (hasLockedMissions) {
        alert('One or more selected missions are locked and cannot be deleted.');
      }

      setSelectedMissions([]); // Clear selected missions

      if (hasDeletedMissions) {
        setMissionsTrigger((prev) => prev + 1); // Increment trigger to force refresh of missions
      }
    } catch (error) {
      console.error(`Error performing action '${action}' on selected missions:`, error);
    }

    handleActionsMenuClose();
  };

  const handleBulkAction = () => {
    console.log("Performing actions on selected missions:", selectedMissions);
  };

  const handleAddMission = () => {
    navigate('/mission/new');
  };

  {/*
  const handleOrgChange = (event) => {
    const orgId = event.target.value;
    updateSelectedOrg(orgId);
  };
  */}

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          padding: '16px',
          backgroundColor: '#f9f9f9',
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            marginBottom: 2,
            color: primaryColor,
            fontWeight: 'bold',
          }}
        >
          Loading Missions...
        </Typography>
        <LinearProgress
          sx={{
            height: 4,
            borderRadius: 4,
            backgroundColor: '#e0e0e0', // Track color
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: '#3f51b5', // Bar color
            },
          }}
        />
      </Box>
    );
  }

  if (error) return <p>{error}</p>;

  return (
    <Box sx={{ padding: 0, backgroundColor: '#f9f9f9' }}>

      {/* Row 1: Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
          paddingLeft: '16px',
          paddingRight: '16px',
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
        }}>
          <MissionIcon sx={{ marginRight: 1, fontSize: '24px', color: primaryColor }} />
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: primaryColor, marginRight: 2 }}
          >
            Missions
          </Typography>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => setView(newView || 'list')}
            sx={{
              marginLeft: 2,
              height: '36px',
              '& .MuiToggleButton-root': {
                color: primaryColor,
                border: '1px solid #dcdcdc',
                padding: '4px 12px',
              },
              '& .MuiToggleButton-root.Mui-selected': {
                color: '#ffffff',
                backgroundColor: primaryColor,
                '&:hover': {
                  backgroundColor: '#1a252f',
                },
              },
            }}
          >
            <ToggleButton value="list">
              <ListIcon />
            </ToggleButton>
            <ToggleButton value="map">
              <MapIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddMission}
        >
          Add Mission
        </Button>
      </Box>

      {/* Row 2: Search and Filter */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        paddingTop: 2,
        marginBottom: 2,
        marginLeft: 0,
        paddingLeft: 2,
        paddingRight: 2,
        width: '100%',
      }}
      >
        {/* Search Box */}
        <Box sx={{ position: 'relative', flexShrink: 0, marginRight: 2 }}>
          <TextField
            label="Search Missions"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: '300px', // Fixed width for the search box
              '& .MuiInputBase-input': { color: primaryColor },
              '& .MuiInputLabel-root': { color: '#6c757d' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#dcdcdc' },
                '&:hover fieldset': { borderColor: primaryColor },
                '&.Mui-focused fieldset': { borderColor: primaryColor },
              },
            }}
          />
          {/* Clear Button */}
          {searchTerm && (
            <Button
              onClick={() => setSearchTerm('')}
              sx={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d',
                minWidth: 'auto',
                padding: 0,
                '&:hover': { color: primaryColor },
              }}
            >
              ✕
            </Button>
          )}
        </Box>

        {/* Filter Dropdown */}
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
          displayEmpty
          sx={{
            width: 150,
            marginLeft: '8px', // Align dropdown to the right of the search box
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff', // White background for dropdown
              color: primaryColor, // Text color for dropdown content
              '& fieldset': {
                borderColor: '#dcdcdc', // Neutral border for default state
              },
              '&:hover fieldset': {
                borderColor: primaryColor, // Darker border on hover
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryColor, // Darker border when focused
              },
            },
            '& .MuiSelect-select': {
              color: primaryColor, // Text color inside dropdown
              padding: '8px 14px', // Match padding with the search box
            },
            '& .MuiMenuItem-root': {
              color: primaryColor, // Text color for dropdown items
              '&:hover': {
                backgroundColor: '#f0f0f0', // Hover effect for dropdown items
              },
            },
          }}
        >
          <MenuItem value="" sx={{ color: '#6c757d' }}>All</MenuItem>
          <MenuItem value="not started" sx={{ color: primaryColor }}>Not Started</MenuItem>
          <MenuItem value="in progress" sx={{ color: primaryColor }}>In Progress</MenuItem>
          <MenuItem value="completed" sx={{ color: primaryColor }}>Completed</MenuItem>
        </Select>
      </Box>

      {/* Actions Button */}
      {view === 'list' && (
        <Box sx={{ marginBottom: 2, textAlign: 'left', paddingLeft: 2 }}>
          <Button
            onClick={handleActionsMenuOpen}
            disabled={selectedMissions.length === 0}
            sx={{
              textTransform: 'none',
              // Blue when enabled, gray when disabled
              //color: selectedMissions.length > 0 ? '#007bff' : '#b0b0b0',
              //cursor: selectedMissions.length > 0 ? 'pointer' : 'not-allowed',
              color: selectedMissions.length > 0 ? '#ffffff' : '#b0b0b0',
              backgroundColor: selectedMissions.length > 0 ? primaryColor : '#e0e0e0', // '#007bff'
              borderRadius: '4px',
              padding: '6px 12px',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: selectedMissions.length > 0 ? '#0056b3' : '#d6d6d6',
              },
              '&.Mui-disabled': {
                cursor: 'not-allowed',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '4px',
            }}
          >
            Actions
          </Button>
          <Menu
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={handleActionsMenuClose}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: '#ffffff', // White background for the menu
                color: primaryColor, // Default text color
              },
            }}
          >
            <MenuItem
              onClick={() => handleAction('Start')}
              sx={{
                '&:hover': {
                  backgroundColor: '#f0f0f0', // Subtle hover background
                  color: '#1a252f', // Slightly darker text on hover
                },
              }}
            >
              <ListItemIcon>
                <StartIcon fontSize="small" sx={{ color: primaryColor }} />
              </ListItemIcon>
              Start
            </MenuItem>
            <MenuItem
              onClick={() => handleAction('Stop')}
              sx={{
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                  color: '#1a252f',
                },
              }}
            >
              <ListItemIcon>
                <StopIcon fontSize="small" sx={{ color: primaryColor }} />
              </ListItemIcon>
              Stop
            </MenuItem>
            <MenuItem
              onClick={() => handleAction('Lock')}
              sx={{
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                  color: '#1a252f',
                },
              }}
            >
              <ListItemIcon>
                <UnlockIcon fontSize="small" sx={{ color: primaryColor }} />
              </ListItemIcon>
              Lock/Unlock
            </MenuItem>
            <MenuItem
              onClick={() => handleAction('Delete')}
              sx={{
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                  color: '#1a252f',
                },
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: primaryColor }} />
              </ListItemIcon>
              Delete
            </MenuItem>
            <MenuItem disabled>
              <Divider sx={{
                //my: 1,
                borderColor: '#4a4a4a',
                borderWidth: '0.5px',
                opacity: 0.8,
                width: '100%'
              }} />
            </MenuItem>
            <MenuItem
              onClick={() => handleAction('Group')}
              sx={{
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                  color: '#1a252f',
                },
              }}
            >
              <ListItemIcon>
                <AddIcon fontSize="small" sx={{ color: primaryColor }} />
              </ListItemIcon>
              Group Selected
            </MenuItem>
            <MenuItem
              onClick={() => handleAction('Ungroup')}
              sx={{
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                  color: '#1a252f',
                },
              }}
            >
              <ListItemIcon>
                <RemoveIcon fontSize="small" sx={{ color: primaryColor }} />
              </ListItemIcon>
              Ungroup Selected
            </MenuItem>
          </Menu>
        </Box>
      )}
      {/* Row 3: Table */}
      {view === 'list' && (
        <Box sx={{
          overflowX: 'auto',
          marginBottom: 6,
          paddingLeft: '8px',
          paddingRight: '14px', // 8 + 6 px
        }}>
          <Table sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#e0e0e0', // Light gray background for the header
                  borderBottom: 'none',
                }}
              >
                {/* Group Indicator Header */}
                <TableCell
                  sx={{
                    width: '6px',
                    padding: 0,
                    backgroundColor: '#f9f9f9',
                    borderBottom: 'none',
                  }}
                />
                <TableCell
                  sx={{
                    width: '6px',
                    padding: 0,
                  }}
                />
                {/* Checkbox Column */}
                <TableCell
                  sx={{
                    width: '24px', // Adjust width for the checkbox
                    padding: '4px 10px', // Padding for alignment
                    //position: 'relative',
                    //'&::before': {
                    //  content: '""', // No separator for the first column
                    //},
                  }}
                >
                  <Checkbox
                    checked={selectedMissions.length === filteredMissions.length && filteredMissions.length > 0}
                    indeterminate={selectedMissions.length > 0 && selectedMissions.length < filteredMissions.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    size="small"
                  />
                </TableCell>

                {/* Dynamic Column Titles */}
                {[
                  { title: 'Mission Name', align: 'left' },
                  { title: 'Location', align: 'left' },
                  { title: 'Scheduled', align: 'left' },
                  { title: 'Flight Date', align: 'left' },
                  { title: 'Remote PIC', align: 'left' },
                  { title: 'State', align: 'center' },
                ].map((column, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      position: 'relative',
                      padding: '4px 8px',
                      textAlign: column.align,
                      fontWeight: 500,
                      color: primaryColor,
                      '&::before': {
                        content: index === 0 ? '""' : '"|"', // No pipe for the first column
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#6c757d',
                        fontWeight: 400,
                      },
                    }}
                  >
                    {column.title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMissions.map((mission) => {
                return (<TableRow
                  key={mission.missionGuid}
                >
                  <TableCell
                    sx={{
                      width: '6px',
                      padding: 0,
                      backgroundColor: getColorForGroup(mission.groupGuid),
                      borderRadius: '4px 0 0 4px', // Rounded corners on the top-left and bottom-left
                      '&:first-of-type': {
                        borderTopLeftRadius: '4px', // Ensure the first row in a group has a top-left radius
                      },
                      '&:last-of-type': {
                        borderBottomLeftRadius: '4px', // Ensure the last row in a group has a bottom-left radius
                      },
                      borderBottom: 'none',
                    }}
                  />

                  <TableCell
                    sx={{
                      width: '6px',
                      padding: 0,
                    }}
                  />

                  <TableCell padding="checkbox" sx={{ padding: '4px 8px' }}>
                    <Checkbox
                      checked={selectedMissions.includes(mission.missionGuid)}
                      onChange={() => handleSelectMission(mission.missionGuid)}
                      color="primary"
                      sx={{ transform: 'scale(0.8)' }}
                    />
                  </TableCell>

                  {/* Mission Name */}
                  <TableCell sx={{ padding: '4px 8px' }}>
                    {getMissionName(mission)}
                  </TableCell>

                  {/* Location */}
                  <TableCell sx={{ padding: '4px 8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <MapPinIcon
                        sx={{
                          color: '#42a5f5',
                          fontSize: '1.25rem',
                          position: 'relative',
                          left: '-3px',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5',
                          color: '#6c757d',
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        {getMissionLocation(mission)}
                      </Typography>
                    </Box>
                  </TableCell >

                  {/* Scheduled */}
                  <TableCell sx={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                    {getDateTime(mission.scheduledAt).map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </TableCell>

                  {/* Flight Date */}
                  <TableCell sx={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                    {getDateTime(mission.startedAt).map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </TableCell>

                  {/* Remote PIC */}
                  <TableCell sx={{ padding: '4px 8px' }}>
                    {mission.remotePic || '-'}
                  </TableCell>

                  {/* Mission State */}
                  <TableCell>{renderIconsForState(mission)}</TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Map View */}
      {view === 'map' && (
        <Box
          ref={mapContainerRef}
          sx={{
            position: 'relative',
            height: '500px',
            width: 'calc(100% - 32px)',
            margin: '0 auto',
            backgroundColor: '#f0f0f0',
            borderRadius: '8px',
          }}
        >
          {/* Linear Progress Indicator */}
          {isDeterminingLocation && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                zIndex: 1000,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
              }}
            />
          )}

          {/* Overlay Button */}
          <Button
            variant="contained"
            onClick={handleCenterOnLocation}
            sx={{
              position: 'absolute',
              bottom: '36px',
              right: '8px',
              zIndex: 1000, // Ensure it overlays the map
              backgroundColor: '#ffffff',
              color: primaryColor,
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            }}
          >
            <MyLocationIcon />
          </Button>
        </Box>
      )}

      {/* Placeholder for Map View */}
      {/*
      {view === 'map' && (
        <Box sx={{ height: '400px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6">Map View Coming Soon</Typography>
        </Box>
      )}
      */}
    </Box>

  );
};

export default Missions;